import {
  CreatePaymentFromPaymentRequestsDocument,
  PaymentsDataCollectionDocument,
  PaymentsPageDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import formatCurrency from 'modules/core/lib/formatCurrency'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

// Minimal type for payment request data needed by the form
interface PaymentRequestForBulkPayment {
  id: number
  amountCents: number
  currency: string
  dueDate?: string | null
}

export interface PaymentFromPaymentRequestsFormValues {
  label: string
  description?: string
  bankAccountId: string
  paymentRequestIds?: number[]
}

export const useCreatePaymentFromPaymentRequestsForm = ({
  onSuccess,
  paymentRequests = [],
}: {
  onSuccess: (id: number) => void
  paymentRequests?: PaymentRequestForBulkPayment[]
}) => {
  const paymentRequestIds = paymentRequests.map((pr) => pr.id)
  const today = moment().format('YYYY-MM-DD')
  const weekStart = moment().startOf('week').format('YYYY-MM-DD')
  const weekEnd = moment().endOf('week').format('YYYY-MM-DD')
  const { company, employee } = useCurrentSession()
  const [createBankingPayment, { loading: loadingCreation }] = useMutation(
    CreatePaymentFromPaymentRequestsDocument,
    {
      extractorFn: (data) => data.bankingMutations.createBankingPayment,
      refetchQueries: [
        PaymentsDataCollectionDocument,
        {
          query: PaymentsPageDocument,
          variables: {
            companyId: company.id,
            featureNames: ['dev_finance_swan_reactivation'],
            todayDate: today,
            weekRange: { from: weekStart, to: weekEnd },
          },
        },
      ],
      awaitRefetchQueries: true,
    }
  )

  const defaultLabel = i18n.t('banking.bulk_payment.forms.default_name', {
    date: moment().format('D MMM YYYY'),
  })

  const defaultDescription = (() => {
    if (paymentRequests.length === 0) return ''

    const totalAmountCents = paymentRequests.reduce((sum, pr) => sum + pr.amountCents, 0)
    const currency = paymentRequests[0]?.currency || 'EUR'
    const formattedAmount = formatCurrency(totalAmountCents, currency)

    const dueDates = paymentRequests
      .map((pr) => pr.dueDate)
      .filter((date): date is string => date !== null && date !== undefined)
      .sort()
    const earliestDueDate = dueDates[0]
    const formattedDueDate = earliestDueDate ? moment(earliestDueDate).format('D MMM YYYY') : null

    if (formattedDueDate) {
      return i18n.t('banking.bulk_payment.forms.default_description_full', {
        count: paymentRequests.length,
        amount: formattedAmount,
        dueDate: formattedDueDate,
      })
    }
    return i18n.t('banking.bulk_payment.forms.default_description_amount', {
      count: paymentRequests.length,
      amount: formattedAmount,
    })
  })()

  const creationForm = useForm<PaymentFromPaymentRequestsFormValues>({
    defaultValues: {
      label: defaultLabel,
      bankAccountId: '',
      description: defaultDescription,
      paymentRequestIds: paymentRequestIds || [],
    },
    validateOnChange: true,
    validate: (values) => {
      const errors: Record<string, string> = {}
      if (!values.label) {
        errors.label = i18n.t('banking.bulk_payment.forms.validation_mandatory')
      } else if (values.label.trim().length <= 0) {
        errors.label = i18n.t('banking.bulk_payment.forms.validation_min_length', { length: 1 })
      } else if (values.label.length > 64) {
        errors.label = i18n.t('banking.bulk_payment.forms.validation_max_length', { length: 64 })
      }

      if (!values.bankAccountId) {
        errors.bankAccountId = i18n.t('banking.bulk_payment.forms.validation_mandatory')
      }
      return errors
    },
    onSubmit: async (values) => {
      const result = await createBankingPayment({
        variables: {
          authorId: employee.id,
          label: values.label,
          description: values.description,
          bankAccountId: Number(values.bankAccountId),
          paymentRequestIds: paymentRequestIds,
        },
      })
      const handled = handleResult(result, (errors) => {
        creationForm.setErrors(errors)
      })
      if (handled.success) {
        const updatedPayment = handled.data.payment
        app.setFlash({
          type: 'correct',
          message: i18n.t('banking.bulk_payment.new_bulk_payment_modal.payment_created'),
        })
        updatedPayment && onSuccess(updatedPayment.id)
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_error'),
        })
      }
      return handled
    },
  })

  return {
    form: creationForm,
    loading: loadingCreation,
  }
}
